import { useCallback, useMemo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PropertyFilterValues } from "../Components/PropertyFilters/PropertyFilters";

const DEFAULT_FILTERS: PropertyFilterValues = {
  minBeds: "0",
  minBaths: "0",
  minYearBuilt: 1800,
  maxYearBuilt: new Date().getFullYear(),
  sortBy: "relevance",
  hasAttic: false,
  hasBasement: false,
  hasDeck: false,
  hasBalcony: false,
  hasGarage: false,
  hasPatio: false,
  hasPool: false,
  hasAirConditioning: false,
};

const SCROLL_STORAGE_KEY = "propertyListScrollPosition";

// Parse URL search params into filter values
function parseFiltersFromUrl(searchParams: URLSearchParams): PropertyFilterValues {
  const currentYear = new Date().getFullYear();

  return {
    minBeds: searchParams.get("minBeds") || "0",
    minBaths: searchParams.get("minBaths") || "0",
    minYearBuilt: parseInt(searchParams.get("minYearBuilt") || "1800", 10),
    maxYearBuilt: parseInt(searchParams.get("maxYearBuilt") || String(currentYear), 10),
    sortBy: searchParams.get("sortBy") || "relevance",
    hasAttic: searchParams.get("hasAttic") === "true",
    hasBasement: searchParams.get("hasBasement") === "true",
    hasDeck: searchParams.get("hasDeck") === "true",
    hasBalcony: searchParams.get("hasBalcony") === "true",
    hasGarage: searchParams.get("hasGarage") === "true",
    hasPatio: searchParams.get("hasPatio") === "true",
    hasPool: searchParams.get("hasPool") === "true",
    hasAirConditioning: searchParams.get("hasAirConditioning") === "true",
  };
}

// Convert filter values to URL search params (only non-default values)
function filtersToSearchParams(filters: PropertyFilterValues): URLSearchParams {
  const params = new URLSearchParams();
  const currentYear = new Date().getFullYear();

  if (filters.minBeds !== "0") params.set("minBeds", filters.minBeds);
  if (filters.minBaths !== "0") params.set("minBaths", filters.minBaths);
  if (filters.minYearBuilt > 1800) params.set("minYearBuilt", String(filters.minYearBuilt));
  if (filters.maxYearBuilt < currentYear) params.set("maxYearBuilt", String(filters.maxYearBuilt));
  if (filters.sortBy !== "relevance") params.set("sortBy", filters.sortBy);
  if (filters.hasAttic) params.set("hasAttic", "true");
  if (filters.hasBasement) params.set("hasBasement", "true");
  if (filters.hasDeck) params.set("hasDeck", "true");
  if (filters.hasBalcony) params.set("hasBalcony", "true");
  if (filters.hasGarage) params.set("hasGarage", "true");
  if (filters.hasPatio) params.set("hasPatio", "true");
  if (filters.hasPool) params.set("hasPool", "true");
  if (filters.hasAirConditioning) params.set("hasAirConditioning", "true");

  return params;
}

interface UseUrlFiltersReturn {
  filters: PropertyFilterValues;
  setFilters: (filters: PropertyFilterValues) => void;
  resetFilters: () => void;
  saveScrollPosition: () => void;
  restoreScrollPosition: () => void;
  page: number;
  setPage: (page: number) => void;
}

export function useUrlFilters(): UseUrlFiltersReturn {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const scrollRestoredRef = useRef(false);

  // Parse filters from current URL
  const filters = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseFiltersFromUrl(searchParams);
  }, [location.search]);

  // Parse page from URL (1-indexed for user-friendly URLs, converted to 0-indexed internally)
  const page = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    return pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
  }, [location.search]);

  // Update URL when filters change
  const setFilters = useCallback(
    (newFilters: PropertyFilterValues) => {
      const params = filtersToSearchParams(newFilters);
      const newSearch = params.toString();
      const currentSearch = new URLSearchParams(location.search).toString();

      // Only navigate if search params actually changed
      if (newSearch !== currentSearch) {
        // Clear saved scroll position when filters change
        sessionStorage.removeItem(SCROLL_STORAGE_KEY);

        navigate(
          {
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : "",
          },
          { replace: true }
        );
      }
    },
    [navigate, location.pathname, location.search]
  );

  // Reset filters to defaults
  const resetFilters = useCallback(() => {
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    navigate(
      {
        pathname: location.pathname,
        search: "",
      },
      { replace: true }
    );
  }, [navigate, location.pathname]);

  // Update page in URL (0-indexed internally, 1-indexed in URL)
  const setPage = useCallback(
    (newPage: number) => {
      const searchParams = new URLSearchParams(location.search);

      if (newPage > 0) {
        searchParams.set("page", String(newPage + 1)); // Convert to 1-indexed for URL
      } else {
        searchParams.delete("page"); // Don't show page=1 in URL
      }

      const newSearch = searchParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        },
        { replace: true }
      );
    },
    [navigate, location.pathname, location.search]
  );

  // Save current scroll position to sessionStorage
  const saveScrollPosition = useCallback(() => {
    const scrollY = window.scrollY;
    const key = `${SCROLL_STORAGE_KEY}_${location.pathname}${location.search}`;
    sessionStorage.setItem(key, String(scrollY));
  }, [location.pathname, location.search]);

  // Restore scroll position from sessionStorage
  const restoreScrollPosition = useCallback(() => {
    if (scrollRestoredRef.current) return;

    const key = `${SCROLL_STORAGE_KEY}_${location.pathname}${location.search}`;
    const savedPosition = sessionStorage.getItem(key);

    if (savedPosition) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedPosition, 10));
        scrollRestoredRef.current = true;
      });
    }
  }, [location.pathname, location.search]);

  // Reset scroll restored flag when URL changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    scrollRestoredRef.current = false;
  }, [location.pathname, location.search]);

  return {
    filters,
    setFilters,
    resetFilters,
    saveScrollPosition,
    restoreScrollPosition,
    page,
    setPage,
  };
}

export { DEFAULT_FILTERS };
